import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { FileText, User, UserCheck, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import { toast } from 'sonner';
import { formatDateFR } from '../../utils/formatFR';

const API = getApiUrl();

/**
 * Dialog admin — accès aux 3 livrables IA d'une séance.
 * L'admin voit TOUT (global + coach_feedback + user_feedback), contrairement aux
 * autres endpoints qui filtrent par rôle. La confidentialité applicative n'est PAS rompue :
 * coach et client ne voient toujours que leur propre feedback via leurs endpoints.
 */
export default function AdminSessionReportDialog({ bookingId, onClose }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    fetch(`${API}/admin/coaches/sessions/${bookingId}/report`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.detail || `Erreur ${r.status}`);
        }
        return r.json();
      })
      .then(setData)
      .catch((err) => {
        setError(err.message);
        toast.error(err.message);
      })
      .finally(() => setLoading(false));
  }, [bookingId, token]);

  const report = data?.report || {};
  const global = report.global_report || {};
  const coachFb = report.coach_feedback || {};
  const userFb = report.user_feedback || {};
  const meta = report._pipeline_meta || {};
  const citationsMeta = report._citations_meta || {};
  const booking = data?.booking || {};

  return (
    <Dialog open={!!bookingId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-4xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden bg-[#0F0F10] border-white/10 text-white p-0 flex flex-col gap-0"
      >
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-white/5 flex-shrink-0">
          <DialogTitle className="text-white text-sm sm:text-lg flex items-center gap-2 flex-wrap pr-8">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
            <span className="truncate">Rapport IA — Administrateur</span>
            {data?.version_count > 1 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-500/15 text-violet-300 border border-violet-500/30">
                v{data.version_count}
              </span>
            )}
            {citationsMeta.grounded !== undefined && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                title={`${citationsMeta.grounded} citations vérifiées, ${citationsMeta.removed} rejetées`}>
                ✓ {citationsMeta.grounded} citations
              </span>
            )}
            {meta.polished && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30">
                Polish FR
              </span>
            )}
          </DialogTitle>
          {booking.coach_name && (
            <div className="text-[11px] sm:text-xs text-zinc-400 pt-1 flex flex-wrap gap-x-2 gap-y-0.5">
              <span className="truncate max-w-full">{booking.coach_name} → {booking.client_name}</span>
              {booking.date && <span>{formatDateFR(booking.date)} {booking.start_time}</span>}
              {booking.theme_label && <span className="text-[#D4AF37] truncate">• {booking.theme_label}</span>}
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3 min-h-0">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-2 text-red-300">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}

        {!loading && !error && data && (
          <Tabs defaultValue="global">
            <div className="overflow-x-auto -mx-4 px-4 pb-1 scrollbar-hide">
              <TabsList className="bg-[#141415] border border-white/5 inline-flex w-auto gap-1 flex-nowrap">
                <TabsTrigger value="global" data-testid="admin-tab-global"
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-xs sm:text-sm flex-shrink-0">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> <span className="hidden xs:inline">Rapport </span>global
                </TabsTrigger>
                <TabsTrigger value="coach" data-testid="admin-tab-coach"
                  className="data-[state=active]:bg-blue-500 data-[state=active]:text-white whitespace-nowrap text-xs sm:text-sm flex-shrink-0">
                  <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Feedback coach
                </TabsTrigger>
                <TabsTrigger value="user" data-testid="admin-tab-user"
                  className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white whitespace-nowrap text-xs sm:text-sm flex-shrink-0">
                  <User className="w-3.5 h-3.5 mr-1.5" /> Feedback utilisateur
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="global" className="space-y-4 mt-4 min-w-0" data-testid="admin-report-global">
              <Section title="Résumé" text={global.resume} />
              {global.theme_analysis && (
                <Callout title="Analyse thématique" accent={global.theme_analysis.ecart_observe ? 'red' : 'emerald'}>
                  <p className="text-xs"><span className="font-semibold">Thème initial :</span> {global.theme_analysis.theme_initial || '—'}</p>
                  <p className="text-xs"><span className="font-semibold">Thème réel :</span> {global.theme_analysis.theme_reel || '—'}</p>
                  <p className="text-xs"><span className="font-semibold">Écart :</span> {global.theme_analysis.ecart_observe ? 'Oui' : 'Non'}</p>
                  {global.theme_analysis.explication_evolution && (
                    <p className="text-xs mt-1 text-zinc-300">{global.theme_analysis.explication_evolution}</p>
                  )}
                </Callout>
              )}
              <ListSection title="Thématiques" items={global.thematiques} />
              <ListSection title="Points clés" items={global.points_cles || global.moments_cles} />
              <SignauxSection signaux={global.signaux_observes} />
              <LongitudinalSection longi={global.analyse_longitudinale} />
              <ListSection title="Axes de travail" items={global.axes_travail} />
              <ListSection title="Prochaines étapes" items={global.prochaines_etapes} />
              <ScoreBlock label="Score progression" score={global.score_progression} />
            </TabsContent>

            <TabsContent value="coach" className="space-y-4 mt-4 min-w-0" data-testid="admin-report-coach">
              <div className="bg-blue-500/5 border-l-2 border-blue-500/40 rounded-r-lg p-3 text-xs text-blue-200">
                🔒 Feedback confidentiel coach (non visible par le coaché)
              </div>
              {coachFb.icf_competences && (
                <div>
                  <h4 className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-2">8 compétences ICF</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {ICF_KEYS.map(({ key, label }) => {
                      const c = coachFb.icf_competences[key];
                      if (!c) return null;
                      const n = c.note || 0;
                      const color = n >= 7 ? 'emerald' : n >= 5 ? 'amber' : 'red';
                      return (
                        <div key={key} className="bg-[#141415] border border-white/5 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-zinc-200 text-xs font-semibold">{label}</span>
                            <span className={`text-${color}-400 text-sm font-bold`}>{n}/10</span>
                          </div>
                          {c.observation && <p className="text-zinc-400 text-[11px] leading-relaxed mb-1.5">{c.observation}</p>}
                          {c.axe_progression && (
                            <p className="text-blue-300/80 text-[11px]">
                              <span className="font-semibold">→ Axe :</span> {c.axe_progression}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {!coachFb.icf_competences && (
                <>
                  <Section title="Écoute" text={coachFb.ecoute} />
                  <Section title="Questionnement" text={coachFb.questionnement} />
                  <Section title="Structure" text={coachFb.structure} />
                  <Section title="Relances" text={coachFb.relances} />
                  <Section title="Capacité de progression" text={coachFb.capacite_progression} />
                </>
              )}
              <ListSection title="Points forts" items={coachFb.points_forts} />
              <ListSection title="Axes d'amélioration" items={coachFb.axes_amelioration} />
              <ScoreBlock label="Prestation coach" score={coachFb.score_prestation} />
            </TabsContent>

            <TabsContent value="user" className="space-y-4 mt-4 min-w-0" data-testid="admin-report-user">
              <div className="bg-emerald-500/5 border-l-2 border-emerald-500/40 rounded-r-lg p-3 text-xs text-emerald-200">
                🔒 Feedback confidentiel utilisateur (non visible par le coach)
              </div>
              <Section title="Engagement" text={userFb.engagement} />
              <Section title="Clarté d'expression" text={userFb.clarte_expression} />
              <Section title="Réflexion" text={userFb.reflexion} />
              <Section title="Progression" text={userFb.progression} />
              <Section title="Posture" text={userFb.posture} />
              <ListSection title="Points forts" items={userFb.points_forts} />
              <ListSection title="Axes d'amélioration" items={userFb.axes_amelioration} />
              <ScoreBlock label="Engagement utilisateur" score={userFb.score_engagement} />
            </TabsContent>
          </Tabs>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ICF_KEYS = [
  { key: 'presence', label: 'Présence' },
  { key: 'ecoute', label: 'Écoute active' },
  { key: 'questionnement', label: 'Questionnement' },
  { key: 'communication_directe', label: 'Communication directe' },
  { key: 'prise_conscience', label: 'Prise de conscience' },
  { key: 'plan_action', label: "Plans d'action" },
  { key: 'responsabilisation', label: 'Responsabilisation' },
  { key: 'gerer_progression', label: 'Gestion progression' },
];

function Section({ title, text }) {
  if (!text) return null;
  return (
    <div className="min-w-0">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-[#D4AF37] mb-1">{title}</h4>
      <p className="text-sm text-zinc-200 leading-relaxed break-words whitespace-pre-wrap">{text}</p>
    </div>
  );
}

function ListSection({ title, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="min-w-0">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-[#D4AF37] mb-2">{title}</h4>
      <ul className="space-y-2">
        {items.map((it, i) => {
          if (typeof it === 'string') return <li key={i} className="text-sm text-zinc-200 break-words">• {it}</li>;
          if (typeof it !== 'object') return null;
          const label = it.point || it.axe || it.nom || '';
          const desc = it.description || it.impact || it.encouragement || it.exercice || '';
          return (
            <li key={i} className="bg-[#141415] border border-white/5 rounded-lg p-2.5 min-w-0">
              {label && <p className="text-sm font-semibold text-zinc-100 break-words">{label}</p>}
              {it.citation && (
                <p className={`text-xs italic mt-1 break-words ${it.citation_grounded === false ? 'text-red-400' : 'text-zinc-400'}`}>
                  « {it.citation} »
                </p>
              )}
              {desc && <p className="text-xs text-zinc-300 mt-1 break-words">{desc}</p>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ScoreBlock({ label, score }) {
  if (!score || score.note == null) return null;
  const n = score.note;
  const color = n >= 7 ? 'emerald' : n >= 5 ? 'amber' : 'red';
  return (
    <div className={`bg-${color}-500/5 border border-${color}-500/20 rounded-xl p-3 flex items-center gap-3 min-w-0`}>
      <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-${color}-500/10 flex items-center justify-center text-${color}-300 font-bold text-xs sm:text-sm flex-shrink-0`}>{n}/10</div>
      <div className="flex-1 min-w-0">
        <p className={`text-${color}-300 text-xs font-semibold uppercase tracking-wider break-words`}>{label}</p>
        {score.justification && <p className="text-zinc-300 text-xs mt-0.5 break-words">{score.justification}</p>}
      </div>
    </div>
  );
}

function Callout({ title, accent, children }) {
  return (
    <div className={`bg-${accent}-500/5 border-l-2 border-${accent}-500/40 rounded-r-lg p-3 min-w-0`}>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-[#D4AF37] mb-1">{title}</h4>
      <div className="break-words">{children}</div>
    </div>
  );
}

function SignauxSection({ signaux }) {
  if (!Array.isArray(signaux) || signaux.length === 0) return null;
  return (
    <div className="min-w-0">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-[#D4AF37] mb-2">Signaux faibles observés</h4>
      <div className="space-y-2">
        {signaux.map((s, i) => (
          <div key={i} className="bg-amber-500/5 border-l-2 border-amber-500/40 rounded-r-lg p-2.5 min-w-0">
            <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 mb-1">
              {s.type?.replace(/_/g, ' ')}
            </span>
            {s.citation && <p className="text-xs italic text-zinc-400 mb-1 break-words">« {s.citation} »</p>}
            {s.lecture_pedagogique && <p className="text-xs text-zinc-300 break-words">{s.lecture_pedagogique}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function LongitudinalSection({ longi }) {
  if (!longi || !Array.isArray(longi.patterns_detectes) || longi.patterns_detectes.length === 0) return null;
  const colors = { progression: 'emerald', regression: 'red', recurrence: 'amber', nouveau_sujet: 'blue' };
  return (
    <div className="min-w-0">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-[#D4AF37] mb-2">Analyse longitudinale</h4>
      <div className="bg-violet-500/5 border-l-2 border-violet-500/40 rounded-r-lg p-3 space-y-2">
        {longi.synthese_evolution && <p className="text-sm text-zinc-200 mb-2 break-words">{longi.synthese_evolution}</p>}
        {longi.patterns_detectes.map((p, i) => {
          const c = colors[p.type] || 'zinc';
          return (
            <div key={i} className="flex items-start gap-2 min-w-0">
              <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0 bg-${c}-500/15 text-${c}-300`}>
                {p.type}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-200 break-words">{p.description}</p>
                {p.reference_session && <p className="text-violet-300 text-[10px] mt-0.5 break-words">→ {p.reference_session}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
